import { useEffect, useState } from "react";
import { Box, Chip, Stack, Typography, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import { ErrorOutline as ErrorOutlineIcon } from "@mui/icons-material";

import { getAppTemplates } from "../../helpers/utils";
import Loading from "../Loading";

/**
 * 2-col responsive card grid for Daari guided sample apps
 * (GET /api/v1/app-templates). Distinct from the static
 * data/samples.json starter drawer — these templates provision
 * their own Daari services (Postgres/S3/DynamoDB/LLM Gateway) as
 * part of app creation.
 *
 * Click a card -> onSelect(template) so the parent can drive the
 * TemplateProvisionDialog.
 */
export default function TemplatePickerGrid({ onSelect }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const [templates, setTemplates] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    getAppTemplates((result, err) => {
      if (cancelled) return;
      if (Array.isArray(result)) {
        setTemplates(result);
      } else {
        setError(err || "Couldn't load templates.");
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          py: 6,
          color: isDarkMode ? "#94a3b8" : "#64748b",
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 32, opacity: 0.6 }} />
        <Typography variant="body2">{error}</Typography>
      </Box>
    );
  }

  if (!templates) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <Loading />
      </Box>
    );
  }

  if (templates.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 6, color: isDarkMode ? "#94a3b8" : "#64748b" }}>
        <Typography variant="body2">No guided templates available yet.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        gap: 2,
      }}
    >
      {templates.map((tpl, idx) => (
        <TemplateCard
          key={tpl.slug || tpl.id || idx}
          template={tpl}
          index={idx}
          isDarkMode={isDarkMode}
          onClick={() => onSelect(tpl)}
        />
      ))}
    </Box>
  );
}

function TemplateCard({ template, index, isDarkMode, onClick }) {
  const [hover, setHover] = useState(false);
  const accent = "#fb923c";
  const services = template?.daariServices || template?.daari_services || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.04 * index, ease: "easeOut" }}
    >
      <Box
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={onClick}
        sx={{
          cursor: "pointer",
          height: "100%",
          borderRadius: 3,
          p: 2.25,
          background: isDarkMode
            ? "linear-gradient(135deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0.02) 100%)"
            : "#ffffff",
          border: `1px solid ${hover ? accent + "70" : isDarkMode ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
          boxShadow: hover
            ? `0 10px 30px rgba(0,0,0,${isDarkMode ? 0.5 : 0.1}), 0 0 0 1px ${accent}45`
            : isDarkMode
            ? "0 2px 10px rgba(0,0,0,0.3)"
            : "0 1px 4px rgba(15,23,42,0.06)",
          transition: "border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
          transform: hover ? "translateY(-2px)" : "translateY(0)",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.4rem",
              flexShrink: 0,
              background: isDarkMode ? "rgba(251,146,60,0.12)" : "rgba(251,146,60,0.12)",
            }}
          >
            {template?.emoji || "🚀"}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "1rem",
                color: isDarkMode ? "rgba(255,255,255,0.95)" : "#1e293b",
              }}
            >
              {template?.displayName || template?.display_name || template?.slug}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.25,
                color: isDarkMode ? "rgba(255,255,255,0.6)" : "#64748b",
                lineHeight: 1.4,
              }}
            >
              {template?.description}
            </Typography>
          </Box>
        </Stack>

        {services.length > 0 && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
            {services.map((svc, i) => (
              <Chip
                key={i}
                label={svc?.type || svc?.varname || svc}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.68rem",
                  fontWeight: 500,
                  background: isDarkMode ? "rgba(255,255,255,0.06)" : "#f1f5f9",
                  color: isDarkMode ? "rgba(255,255,255,0.75)" : "#475569",
                  border: isDarkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0",
                }}
              />
            ))}
          </Stack>
        )}
      </Box>
    </motion.div>
  );
}
